import {
  SlotComponentDef,
  SlotComponentGroup,
  SlotComponentGroupMeta,
  AssetFormat,
} from '../../core/types/slot-asset-generator.types';

// === Group Metadata (accordion order) ===

export const SLOT_COMPONENT_GROUPS: SlotComponentGroupMeta[] = [
  { id: SlotComponentGroup.Scene, label: 'SCENE', collapsed: false },
  { id: SlotComponentGroup.Loading, label: 'LOADING', collapsed: true },
  { id: SlotComponentGroup.Decoration, label: 'DECORATION', collapsed: true },
  { id: SlotComponentGroup.Slot, label: 'SLOT MACHINE', collapsed: true },
  { id: SlotComponentGroup.Symbols, label: 'SYMBOLS', collapsed: false },
  { id: SlotComponentGroup.TopUI, label: 'TOP UI', collapsed: true },
  { id: SlotComponentGroup.BottomUI, label: 'BOTTOM UI', collapsed: true },
  { id: SlotComponentGroup.Win, label: 'WIN ANIMATIONS', collapsed: true },
  { id: SlotComponentGroup.Popups, label: 'POPUPS', collapsed: true },
  { id: SlotComponentGroup.Typography, label: 'TYPOGRAPHY', collapsed: true },
];

// === Subgroup Labels ===

export const SYMBOL_SUBGROUP_LABELS: Record<string, string> = {
  high: 'High Value',
  low: 'Low Value',
  special: 'Special',
  golden: 'Golden Variants',
};

export const BOTTOM_UI_SUBGROUP_LABELS: Record<string, string> = {
  main: 'Main Panel',
  second: 'Second Panel',
  info: 'Info Displays',
};

export const POPUP_SUBGROUP_LABELS: Record<string, string> = {
  'bet-option': 'Bet Option',
  'spin-option': 'Spin Option',
  'symbol-info': 'Symbol Info',
  'buy-feature': 'Buy Feature',
  'history': 'History',
  'error': 'Error',
};

// === Default Component Registry (93 components) ===

export const DEFAULT_SLOT_COMPONENTS: SlotComponentDef[] = [
  // --- SCENE (1) ---
  {
    id: 'background',
    name: 'Background',
    group: SlotComponentGroup.Scene,
    width: 720,
    height: 1280,
    format: AssetFormat.Jpg,
    assetPath: 'spines/background/mahjong1-background',
    notes: 'Animated Spine skeleton, full-screen background',
    referenceImages: [],
  },

  // --- LOADING (4) ---
  {
    id: 'loading-bg',
    name: 'Loading BG',
    group: SlotComponentGroup.Loading,
    width: 720,
    height: 1280,
    format: AssetFormat.Jpg,
    assetPath: 'preload/loading-bg.jpg',
    notes: 'Loading screen background',
    referenceImages: [],
  },
  {
    id: 'progress-bar',
    name: 'Progress Bar',
    group: SlotComponentGroup.Loading,
    width: 600,
    height: 30,
    format: AssetFormat.PngTransparent,
    assetPath: 'preload/bar.png',
    notes: 'Animated fill bar',
    referenceImages: [],
  },
  {
    id: 'progress-bar-bg',
    name: 'Progress Bar BG',
    group: SlotComponentGroup.Loading,
    width: 600,
    height: 40,
    format: AssetFormat.PngTransparent,
    assetPath: 'preload/bar-bg.png',
    notes: 'Behind progress bar',
    referenceImages: [],
  },
  {
    id: 'progress-bar-mask',
    name: 'Progress Bar Mask',
    group: SlotComponentGroup.Loading,
    width: 600,
    height: 30,
    format: AssetFormat.PngTransparent,
    assetPath: 'preload/bar-mask.png',
    notes: 'Mask shape for progress bar',
    referenceImages: [],
  },

  // --- DECORATION (2) ---
  {
    id: 'top-decoration',
    name: 'Top Decoration Banner',
    group: SlotComponentGroup.Decoration,
    width: 720,
    height: 120,
    format: AssetFormat.PngTransparent,
    assetPath: 'dump/top-decoration.png',
    notes: 'Thematic overlay above grid (coins, ornaments)',
    referenceImages: [],
  },
  {
    id: 'character-mascot',
    name: 'Character / Mascot Art',
    group: SlotComponentGroup.Decoration,
    width: 200,
    height: 200,
    format: AssetFormat.PngTransparent,
    assetPath: 'dump/character-mascot.png',
    notes: 'Game character decoration element',
    referenceImages: [],
  },

  // --- SLOT MACHINE (7) ---
  {
    id: 'machine-frame',
    name: 'Machine Frame / Reel BG',
    group: SlotComponentGroup.Slot,
    width: 720,
    height: 650,
    format: AssetFormat.PngTransparent,
    assetPath: 'ui/scene/bot-ui-base.png',
    notes: 'Slot area container (anchored bottom-center)',
    referenceImages: [],
  },
  {
    id: 'machine-frame-bonus',
    name: 'Slot Machine (Bonus mode)',
    group: SlotComponentGroup.Slot,
    width: 720,
    height: 650,
    format: AssetFormat.PngTransparent,
    assetPath: 'ui/scene/bot-ui-bonus.png',
    notes: 'Bonus/free-spin state variant',
    referenceImages: [],
  },
  {
    id: 'cell-win-highlight',
    name: 'Cell Win Highlight',
    group: SlotComponentGroup.Slot,
    width: 86,
    height: 86,
    format: AssetFormat.PngTransparent,
    assetPath: 'dump/cell-win-highlight.png',
    notes: 'Golden glow border overlay for winning cells',
    referenceImages: [],
  },
  {
    id: 'win-banner',
    name: 'Win Banner Ribbon',
    group: SlotComponentGroup.Slot,
    width: 400,
    height: 60,
    format: AssetFormat.PngTransparent,
    assetPath: 'dump/win-banner.png',
    notes: 'Gold gradient pill/ribbon (win display)',
    referenceImages: [],
  },
  {
    id: 'reward-info-base',
    name: 'Reward Info Base',
    group: SlotComponentGroup.Slot,
    width: null,
    height: null,
    format: AssetFormat.PngTransparent,
    assetPath: 'dump/reward-info-base.png',
    notes: 'Win display backing (y: 305)',
    referenceImages: [],
  },
  {
    id: 'reward-info-bonus',
    name: 'Reward Info Bonus',
    group: SlotComponentGroup.Slot,
    width: null,
    height: null,
    format: AssetFormat.PngTransparent,
    assetPath: 'dump/reward-info-bonus.png',
    notes: 'Bonus win backing',
    referenceImages: [],
  },
  {
    id: 'reward-info-mask',
    name: 'Reward Info Mask',
    group: SlotComponentGroup.Slot,
    width: null,
    height: null,
    format: AssetFormat.PngTransparent,
    assetPath: 'ui/scene/reward-info-mask.png',
    notes: 'Mask overlay for hint text',
    referenceImages: [],
  },

  // --- SYMBOLS: High (4) ---
  {
    id: 'h-1', name: 'Symbol H-1', group: SlotComponentGroup.Symbols, subgroup: 'high',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/h-1.png', notes: 'High-value 1', referenceImages: [], symbolId: 1,
  },
  {
    id: 'h-2', name: 'Symbol H-2', group: SlotComponentGroup.Symbols, subgroup: 'high',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/h-2.png', notes: 'High-value 2', referenceImages: [], symbolId: 2,
  },
  {
    id: 'h-3', name: 'Symbol H-3', group: SlotComponentGroup.Symbols, subgroup: 'high',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/h-3.png', notes: 'High-value 3', referenceImages: [], symbolId: 3,
  },
  {
    id: 'h-4', name: 'Symbol H-4', group: SlotComponentGroup.Symbols, subgroup: 'high',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/h-4.png', notes: 'High-value 4', referenceImages: [], symbolId: 4,
  },

  // --- SYMBOLS: Low (5) ---
  {
    id: 'l-1', name: 'Symbol L-1', group: SlotComponentGroup.Symbols, subgroup: 'low',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-1.png', notes: 'Low-value 1', referenceImages: [], symbolId: 11,
  },
  {
    id: 'l-2', name: 'Symbol L-2', group: SlotComponentGroup.Symbols, subgroup: 'low',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-2.png', notes: 'Low-value 2', referenceImages: [], symbolId: 12,
  },
  {
    id: 'l-3', name: 'Symbol L-3', group: SlotComponentGroup.Symbols, subgroup: 'low',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-3.png', notes: 'Low-value 3', referenceImages: [], symbolId: 13,
  },
  {
    id: 'l-4', name: 'Symbol L-4', group: SlotComponentGroup.Symbols, subgroup: 'low',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-4.png', notes: 'Low-value 4', referenceImages: [], symbolId: 14,
  },
  {
    id: 'l-5', name: 'Symbol L-5', group: SlotComponentGroup.Symbols, subgroup: 'low',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-5.png', notes: 'Low-value 5', referenceImages: [], symbolId: 15,
  },

  // --- SYMBOLS: Special (2) ---
  {
    id: 'wild', name: 'Symbol Wild', group: SlotComponentGroup.Symbols, subgroup: 'special',
    width: 175, height: 225, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/wild.png', notes: 'zIndex: 10000', referenceImages: [], symbolId: 50,
  },
  {
    id: 'scatter', name: 'Symbol Scatter', group: SlotComponentGroup.Symbols, subgroup: 'special',
    width: 200, height: 225, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/scatter.png', notes: 'zIndex: 10000', referenceImages: [], symbolId: 31,
  },

  // --- SYMBOLS: Golden (9) ---
  {
    id: 'h-1-g', name: 'Golden H-1', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/h-1-g.png', notes: 'Golden variant of H-1', referenceImages: [],
    symbolId: 55, deriveFrom: 'h-1',
  },
  {
    id: 'h-2-g', name: 'Golden H-2', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/h-2-g.png', notes: 'Golden variant of H-2', referenceImages: [],
    symbolId: 56, deriveFrom: 'h-2',
  },
  {
    id: 'h-3-g', name: 'Golden H-3', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/h-3-g.png', notes: 'Golden variant of H-3', referenceImages: [],
    symbolId: 57, deriveFrom: 'h-3',
  },
  {
    id: 'h-4-g', name: 'Golden H-4', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/h-4-g.png', notes: 'Golden variant of H-4', referenceImages: [],
    symbolId: 58, deriveFrom: 'h-4',
  },
  {
    id: 'l-1-g', name: 'Golden L-1', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-1-g.png', notes: 'Golden variant of L-1', referenceImages: [],
    symbolId: 65, deriveFrom: 'l-1',
  },
  {
    id: 'l-2-g', name: 'Golden L-2', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-2-g.png', notes: 'Golden variant of L-2', referenceImages: [],
    symbolId: 66, deriveFrom: 'l-2',
  },
  {
    id: 'l-3-g', name: 'Golden L-3', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-3-g.png', notes: 'Golden variant of L-3', referenceImages: [],
    symbolId: 67, deriveFrom: 'l-3',
  },
  {
    id: 'l-4-g', name: 'Golden L-4', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-4-g.png', notes: 'Golden variant of L-4', referenceImages: [],
    symbolId: 68, deriveFrom: 'l-4',
  },
  {
    id: 'l-5-g', name: 'Golden L-5', group: SlotComponentGroup.Symbols, subgroup: 'golden',
    width: 140, height: 180, format: AssetFormat.PngTransparent,
    assetPath: 'symbols/static/l-5-g.png', notes: 'Golden variant of L-5', referenceImages: [],
    symbolId: 69, deriveFrom: 'l-5',
  },

  // --- TOP UI (2) ---
  {
    id: 'multiplier-base', name: 'Multiplier Base', group: SlotComponentGroup.TopUI,
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'dump/multiplierBase.png', notes: 'y = -490, center', referenceImages: [],
  },
  {
    id: 'multiplier-bonus', name: 'Multiplier Bonus', group: SlotComponentGroup.TopUI,
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'dump/multiplierBonus.png', notes: 'Bonus state variant', referenceImages: [],
  },

  // --- BOTTOM UI: Main (9) ---
  {
    id: 'spin-button-default', name: 'Spin Button (Default)', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: 200, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'dump/spinButtonDefault.png', notes: 'Center, main CTA', referenceImages: [],
  },
  {
    id: 'spin-button-press', name: 'Spin Button (Press)', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: 200, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'dump/spinButtonPress.png', notes: 'Pressed state', referenceImages: [],
  },
  {
    id: 'spin-button-stop', name: 'Spin Button (Stop)', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: 200, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'dump/spinButtonStop.png', notes: 'Auto-spin stop', referenceImages: [],
  },
  {
    id: 'spin-button-disable', name: 'Spin Button (Disable)', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: 200, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'dump/spinButtonDisable.png', notes: 'Disabled state', referenceImages: [],
  },
  {
    id: 'bet-opt-btn', name: 'Bet Option Button', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: 80, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/bet-opt-btn.png', notes: 'Right of spin', referenceImages: [],
  },
  {
    id: 'spin-opt-btn', name: 'Spin Option Button', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: 80, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/spin-opt-btn.png', notes: 'Left of spin (turbo/auto)', referenceImages: [],
  },
  {
    id: 'buy-feature-btn', name: 'Buy Feature Button', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/buy-feature-btn.png', notes: 'y = -430', referenceImages: [],
  },
  {
    id: 'setting-btn', name: 'Settings Button', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/setting-btn.png', notes: 'Top-right corner', referenceImages: [],
  },
  {
    id: 'setting-close-btn', name: 'Settings Close Button', group: SlotComponentGroup.BottomUI, subgroup: 'main',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/setting-close-btn.png', notes: 'Close settings', referenceImages: [],
  },

  // --- BOTTOM UI: Second Panel (4) ---
  {
    id: 'info-btn', name: 'Game Info Button', group: SlotComponentGroup.BottomUI, subgroup: 'second',
    width: 80, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/info-btn.png', notes: '', referenceImages: [],
  },
  {
    id: 'sound-btn-on', name: 'Sound Button (On)', group: SlotComponentGroup.BottomUI, subgroup: 'second',
    width: 80, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/sound-btn-on.png', notes: '', referenceImages: [],
  },
  {
    id: 'sound-btn-off', name: 'Sound Button (Off)', group: SlotComponentGroup.BottomUI, subgroup: 'second',
    width: 80, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/sound-btn-off.png', notes: '', referenceImages: [],
  },
  {
    id: 'history-btn', name: 'History Button', group: SlotComponentGroup.BottomUI, subgroup: 'second',
    width: 80, height: 80, format: AssetFormat.PngTransparent,
    assetPath: 'ui/buttons/history-btn.png', notes: '', referenceImages: [],
  },

  // --- BOTTOM UI: Info Displays (4) ---
  {
    id: 'balance-icon', name: 'Balance Icon', group: SlotComponentGroup.BottomUI, subgroup: 'info',
    width: 60, height: 60, format: AssetFormat.PngTransparent,
    assetPath: 'ui/balance-icon.png', notes: 'Wallet icon', referenceImages: [],
  },
  {
    id: 'win-icon', name: 'Win Icon', group: SlotComponentGroup.BottomUI, subgroup: 'info',
    width: 60, height: 60, format: AssetFormat.PngTransparent,
    assetPath: 'ui/win-icon.png', notes: 'Trophy icon', referenceImages: [],
  },
  {
    id: 'bg-number', name: 'BG Number', group: SlotComponentGroup.BottomUI, subgroup: 'info',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/bg-number.png', notes: 'Number backdrop', referenceImages: [],
  },
  {
    id: 'simple-bet-ui-bg', name: 'Simple Bet UI BG', group: SlotComponentGroup.BottomUI, subgroup: 'info',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/simple-bet-ui-bg.png', notes: 'Alt bet display', referenceImages: [],
  },

  // --- WIN ANIMATIONS (5) ---
  {
    id: 'big-win', name: 'Big Win Animation', group: SlotComponentGroup.Win,
    width: 720, height: 1280, format: AssetFormat.PngKeyframe,
    assetPath: 'coc-mahjong1-big-win.skel', notes: 'Full-screen hero image (static key frame)', referenceImages: [],
  },
  {
    id: 'total-win', name: 'Total Win Animation', group: SlotComponentGroup.Win,
    width: 720, height: 1280, format: AssetFormat.PngKeyframe,
    assetPath: 'coc-mahjong1-total-win.skel', notes: 'End-of-spin banner (static key frame)', referenceImages: [],
  },
  {
    id: 'free-spin-intro', name: 'Free Spin Intro', group: SlotComponentGroup.Win,
    width: 720, height: 1280, format: AssetFormat.PngKeyframe,
    assetPath: 'coc-mahjong1-total-freespin.skel', notes: 'Free spin entry splash (static key frame)', referenceImages: [],
  },
  {
    id: 'symbol-win-fx', name: 'Symbol Win FX', group: SlotComponentGroup.Win,
    width: 200, height: 200, format: AssetFormat.PngKeyframe,
    assetPath: 'symbols.skel', notes: 'Per-symbol glow/burst frame', referenceImages: [],
  },
  {
    id: 'coin-burst-fx', name: 'Coin Burst FX', group: SlotComponentGroup.Win,
    width: 400, height: 400, format: AssetFormat.PngKeyframe,
    assetPath: '', notes: 'Coin explosion / particle burst for big win celebrations', referenceImages: [],
  },

  // --- POPUPS: Bet Option (10) ---
  {
    id: 'popup-bg', name: 'Popup BG', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/popup-bg.png', notes: 'Shared with Spin Option Popup', referenceImages: [],
  },
  {
    id: 'bo-add-btn', name: 'Add Button', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/bo-add-btn.png', notes: 'Hold-to-repeat (200ms interval)', referenceImages: [],
  },
  {
    id: 'bo-min-btn', name: 'Min Button', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/bo-min-btn.png', notes: 'Hold-to-repeat (200ms interval)', referenceImages: [],
  },
  {
    id: 'bo-bar', name: 'Slider Bar (Fill)', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: 396, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/bo-bar.png', notes: 'Progress fill, max width 396px', referenceImages: [],
  },
  {
    id: 'bo-bar-bg', name: 'Slider BG (Track)', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: 396, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/bo-bar-bg.png', notes: 'Background track', referenceImages: [],
  },
  {
    id: 'bo-bar-slider', name: 'Slider Handle', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/bo-bar-slider.png', notes: 'Draggable thumb', referenceImages: [],
  },
  {
    id: 'quick-auto-off', name: 'Quick Auto Off', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/quick-auto-off.png', notes: 'Bet preset button (unselected)', referenceImages: [],
  },
  {
    id: 'quick-auto-on', name: 'Quick Auto On', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/quick-auto-on.png', notes: 'Bet preset button (selected)', referenceImages: [],
  },
  {
    id: 'confirm-btn', name: 'Confirm Button', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/confirm-btn.png', notes: 'Shared with Spin Option Popup', referenceImages: [],
  },
  {
    id: 'close-btn', name: 'Close Button', group: SlotComponentGroup.Popups, subgroup: 'bet-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/close-btn.png', notes: 'Shared (scale: 0.7)', referenceImages: [],
  },

  // --- POPUPS: Spin Option (4) ---
  {
    id: 'so-switch-off', name: 'SO Switch Off', group: SlotComponentGroup.Popups, subgroup: 'spin-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/so-switch-off.png', notes: 'Toggle off state', referenceImages: [],
  },
  {
    id: 'so-switch-on', name: 'SO Switch On', group: SlotComponentGroup.Popups, subgroup: 'spin-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/so-switch-on.png', notes: 'Toggle on state', referenceImages: [],
  },
  {
    id: 'so-quick-auto-off', name: 'Quick Auto Off (SO)', group: SlotComponentGroup.Popups, subgroup: 'spin-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/quick-auto-off.png', notes: 'Auto-spin count (unselected)', referenceImages: [],
  },
  {
    id: 'so-quick-auto-on', name: 'Quick Auto On (SO)', group: SlotComponentGroup.Popups, subgroup: 'spin-option',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/quick-auto-on.png', notes: 'Auto-spin count (selected)', referenceImages: [],
  },

  // --- POPUPS: Symbol Info (1) ---
  {
    id: 'symbol-info-sheet', name: 'Symbol Info Sheet', group: SlotComponentGroup.Popups, subgroup: 'symbol-info',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/spin-bet-opt-popups/symbol-information.png', notes: 'Paytable overlay (scale: 0.5)', referenceImages: [],
  },

  // --- POPUPS: Buy Feature (3) ---
  {
    id: 'bf-feature-bg', name: 'Buy Feature BG', group: SlotComponentGroup.Popups, subgroup: 'buy-feature',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'dump/bf-feature-bg.png', notes: 'Feature item background', referenceImages: [],
  },
  {
    id: 'bf-confirm-button', name: 'Buy Confirm Button', group: SlotComponentGroup.Popups, subgroup: 'buy-feature',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'dump/bf-confirm-button.png', notes: 'Confirm purchase', referenceImages: [],
  },
  {
    id: 'bf-cancel-button', name: 'Buy Cancel Button', group: SlotComponentGroup.Popups, subgroup: 'buy-feature',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'dump/bf-cancel-button.png', notes: 'Cancel / close', referenceImages: [],
  },

  // --- POPUPS: History (14) ---
  {
    id: 'history-bg', name: 'History BG', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/bg-hisroty.png', notes: 'Main background (typo in actual filename)', referenceImages: [],
  },
  {
    id: 'history-log', name: 'Log Button', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/log.png', notes: 'Date range filter trigger', referenceImages: [],
  },
  {
    id: 'history-arrow1', name: 'Arrow 1', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/arrow1.png', notes: 'Date pagination nav', referenceImages: [],
  },
  {
    id: 'history-arrow2', name: 'Arrow 2', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/arrow2.png', notes: 'Date pagination nav', referenceImages: [],
  },
  {
    id: 'history-arrow3', name: 'Arrow 3', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/arrow3.png', notes: 'Date pagination nav', referenceImages: [],
  },
  {
    id: 'history-back-btn', name: 'Back Button', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/back-btn.png', notes: 'Back navigation', referenceImages: [],
  },
  {
    id: 'history-bg-status', name: 'Status BG', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/bg-status.png', notes: 'Status indicator background', referenceImages: [],
  },
  {
    id: 'history-break-line', name: 'Break Line', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/break-line.png', notes: 'Visual divider', referenceImages: [],
  },
  {
    id: 'history-button1', name: 'Button 1', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/button1.png', notes: 'Date range confirm', referenceImages: [],
  },
  {
    id: 'history-button2', name: 'Button 2', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/button2.png', notes: 'Alternative action', referenceImages: [],
  },
  {
    id: 'history-button-item', name: 'Button Item', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/button-item.png', notes: 'History transaction item (ScrollBox)', referenceImages: [],
  },
  {
    id: 'history-close-x', name: 'Close X', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/x.png', notes: 'Close popup', referenceImages: [],
  },
  {
    id: 'history-loading', name: 'Loading Indicator', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/loading.png', notes: 'Loading animation', referenceImages: [],
  },
  {
    id: 'history-fade-fx', name: 'Fade FX', group: SlotComponentGroup.Popups, subgroup: 'history',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/history-popup/fade-fx.png', notes: 'Fade transition overlay', referenceImages: [],
  },

  // --- POPUPS: Error (2) ---
  {
    id: 'error-popup-bg', name: 'Error BG', group: SlotComponentGroup.Popups, subgroup: 'error',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/error-popup/error-popup.png', notes: 'Error dialog background', referenceImages: [],
  },
  {
    id: 'error-done-btn', name: 'Done Button', group: SlotComponentGroup.Popups, subgroup: 'error',
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'ui/error-popup/done-btn.png', notes: 'Dismiss button', referenceImages: [],
  },

  // --- TYPOGRAPHY (1) ---
  {
    id: 'bitmap-font', name: 'Bitmap Font', group: SlotComponentGroup.Typography,
    width: null, height: null, format: AssetFormat.PngTransparent,
    assetPath: 'fonts/supercell-white-small.fnt', notes: 'BitmapText font (.fnt + .png)', referenceImages: [],
  },
];

// === Helper Functions ===

export function getComponentsByGroup(group: SlotComponentGroup): SlotComponentDef[] {
  return DEFAULT_SLOT_COMPONENTS.filter((c) => c.group === group);
}

export function getComponentsBySubgroup(group: SlotComponentGroup, subgroup: string): SlotComponentDef[] {
  return DEFAULT_SLOT_COMPONENTS.filter((c) => c.group === group && c.subgroup === subgroup);
}

export function getGoldenVariant(baseId: string): SlotComponentDef | undefined {
  return DEFAULT_SLOT_COMPONENTS.find((c) => c.deriveFrom === baseId);
}

export function getBaseSymbol(goldenId: string): SlotComponentDef | undefined {
  const golden = DEFAULT_SLOT_COMPONENTS.find((c) => c.id === goldenId);
  if (!golden?.deriveFrom) return undefined;
  return DEFAULT_SLOT_COMPONENTS.find((c) => c.id === golden.deriveFrom);
}

export function getComponentById(id: string): SlotComponentDef | undefined {
  return DEFAULT_SLOT_COMPONENTS.find((c) => c.id === id);
}

/** Groups that have subgroups requiring nested accordion */
export const GROUPS_WITH_SUBGROUPS: Record<string, Record<string, string>> = {
  [SlotComponentGroup.Symbols]: SYMBOL_SUBGROUP_LABELS,
  [SlotComponentGroup.BottomUI]: BOTTOM_UI_SUBGROUP_LABELS,
  [SlotComponentGroup.Popups]: POPUP_SUBGROUP_LABELS,
};
